/**
 * Claves de cache de TanStack Query, centralizadas por dominio.
 *
 * Regla: usar siempre campos primitivos (uid, adminUid, rutaUid) como parte
 * de la key, nunca objetos completos (userData, usuarioRuta, selectedRuta) -
 * un objeto se recrea en cada render y rompe la igualdad de la key aunque su
 * contenido no haya cambiado, provocando refetches espurios.
 */

export const queryKeys = {
  authUser: (uid: string | undefined) => ["authUser", uid] as const,

  adminUsersList: () => ["adminUsersList"] as const,

  usuarioRuta: (currentUserUid: string | undefined) => ["usuarioRuta", currentUserUid] as const,

  rutas: (adminUid: string | undefined) => ["rutas", adminUid] as const,

  clientes: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["clientes", adminUid, rutaUid] as const,

  allClients: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["allClients", adminUid, rutaUid] as const,

  // Vista de detalle de una ruta ajena (VerDetallesRutasModal): distinta de
  // `clientes` porque su fetch incluye recalculo de cuotas y auto-archivado
  // de clientes que ya terminaron de pagar, un procesamiento que ClientesProvider
  // no hace - unificar la key haria que un consumidor sirviera datos del otro
  // sin pasar por ese procesamiento.
  clientesDetalleRuta: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["clientesDetalleRuta", adminUid, rutaUid] as const,

  movimientosDelDia: (
    adminUid: string | undefined,
    rutaUid: string | undefined,
    fechaHoy: string,
  ) => ["movimientosDelDia", adminUid, rutaUid, fechaHoy] as const,

  // Prefijo de movimientosDelDia sin la fecha - usar solo para invalidar
  // desde una mutacion que no conoce (o no le importa) el fechaHoy exacto de
  // ClientesProvider; TanStack Query matchea por prefijo de key.
  movimientosDelDiaByRuta: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["movimientosDelDia", adminUid, rutaUid] as const,

  // Total de gastos del dia de una ruta (EstadisticasPage/EstadisticasModal).
  // Comparte dominio con `gastos` pero es una agregacion distinta (suma del
  // dia, no el historial paginado), asi que vive en su propia key.
  gastosDelDia: (adminUid: string | undefined, rutaUid: string | undefined, fechaHoy: string) =>
    ["gastosDelDia", adminUid, rutaUid, fechaHoy] as const,

  gastosDelDiaByRuta: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["gastosDelDia", adminUid, rutaUid] as const,

  movimientos: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["movimientos", adminUid, rutaUid] as const,

  gastos: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["gastos", adminUid, rutaUid] as const,

  cierreCajaHoy: (adminUid: string | undefined, rutaUid: string | undefined, fechaClave: string) =>
    ["cierreCajaHoy", adminUid, rutaUid, fechaClave] as const,

  cierresCajaHistorial: (adminUid: string | undefined, rutaUid: string | undefined) =>
    ["cierresCajaHistorial", adminUid, rutaUid] as const,

  adminPass: () => ["adminPass"] as const,

  rutasPass: (uid: string | undefined) => ["rutasPass", uid] as const,
};
